import { useMemo } from 'react';

import { isFeatureEnabled } from '@website/config/featureFlags';
import { extractCity } from '@website/lib/address';
import { Activity } from '@website/services/activities.service';
import { type SearchHub, hubSelectionId } from '@website/services/search.service';
import { calculateClusterThreshold, calculateDistance, hasRealCoordinates } from '@website/utils/mapUtils';

interface UseFilteredActivitiesParams {
  /** Not-bookable-yet activities (availability 'none') get muted pins, unless a bookable one shares them. */
  activities?: Activity[];
  /** The Hubs found: one pin each, holding the activities found there. */
  hubs?: SearchHub[];
  zoom?: number | null;
}

/** How close (in degrees, ~10 m) an activity's location must be to its Hub to sit in the Hub's pin: a placement copies the Hub's address. */
const AT_HUB_TOLERANCE = 0.0001;

/** One bookable slot-location of an activity, as a map point. */
interface LocationPoint {
  activity: Activity;
  lat: number;
  lng: number;
}

/** A co-located group carrying more than one distinct activity (clustered pin). */
interface ClusteredMarker {
  id: string;
  latitude: number;
  longitude: number;
  name: string;
  count: number;
  activityIds: string[];
  /** Every activity at this pin is not bookable yet. */
  isMuted: boolean;
  activities: Array<{
    id: string;
    title: string;
    professionalName: string;
    startingPrice?: number | null;
    currency?: string;
    imageUrl?: string;
    isAtHome?: boolean;
    homeRadius?: number;
  }>;
}

/**
 * A Hub's pin: it holds the activities found at the Hub. `activities` / `activityIds` list the Hub
 * itself first (its selection id), so a tap opens on the Hub and the group arrows go on to its
 * activities; `count` counts the Hub and its activities.
 */
interface HubMarker extends ClusteredMarker {
  variant: 'hub';
  hubId: string;
}

/** A pin carrying a single distinct activity. */
interface SingleMarker {
  id: string;
  latitude: number;
  longitude: number;
  name: string;
  isAtHome?: boolean;
  homeRadius?: number;
  isMuted: boolean;
  /** Always absent on a single pin; declared so consumers can presence-narrow the
   *  union on `activityIds` (a clustered pin carries the real list). */
  activityIds?: undefined;
}

/**
 * One marker fed to GoogleMapWrapper. Structurally a subset of that component's
 * `Location` shape; consumers narrow on the clustered fields (`count` /
 * `activityIds`).
 */
type MarkerLocation = ClusteredMarker | SingleMarker | HubMarker;

/**
 * Build one map marker for a group of co-located points. The activity LIST is
 * de-duplicated to one row per activity (F10): with several pins per activity a
 * single activity can contribute more than one point, but it must appear at most
 * once in a marker's `activities` / `activityIds`. A marker carrying exactly one
 * distinct activity collapses to the single-activity shape (no count/list).
 */
function buildMarker(id: string, latitude: number, longitude: number, points: LocationPoint[]): MarkerLocation {
  const byId = new Map<string, Activity>();
  for (const p of points) {
    if (!byId.has(p.activity.id)) byId.set(p.activity.id, p.activity);
  }
  const distinct = Array.from(byId.values());
  const isMuted = distinct.every((a) => a.availability === 'none');

  if (distinct.length > 1) {
    return {
      id,
      latitude,
      longitude,
      name: `${distinct.length} activities`,
      count: distinct.length,
      activityIds: distinct.map((a) => a.id),
      isMuted,
      activities: distinct.map((a) => ({
        id: a.id,
        title: a.title,
        professionalName: a.professionalName,
        startingPrice: a.startingPrice,
        currency: 'CAD',
        imageUrl: a.thumbnailUrl,
        isAtHome: a.isAtHome,
        homeRadius: a.homeRadius
      }))
    };
  }

  const activity = distinct[0];
  return {
    id: distinct.length === 1 ? activity.id : id,
    latitude,
    longitude,
    name: activity.title,
    isAtHome: activity.isAtHome,
    homeRadius: activity.homeRadius,
    isMuted
  };
}

function buildHubMarker(hub: SearchHub, activities: Activity[]): HubMarker {
  const selectionId = hubSelectionId(hub.id);
  return {
    id: selectionId,
    hubId: hub.id,
    variant: 'hub',
    latitude: hub.coordinates.latitude,
    longitude: hub.coordinates.longitude,
    name: hub.name,
    count: activities.length + 1,
    activityIds: [selectionId, ...activities.map((a) => a.id)],
    isMuted: false,
    activities: [
      { id: selectionId, title: hub.name, professionalName: '', imageUrl: hub.coverImageUrl ?? undefined },
      ...activities.map((a) => ({
        id: a.id,
        title: a.title,
        professionalName: a.professionalName,
        startingPrice: a.startingPrice,
        currency: 'CAD',
        imageUrl: a.thumbnailUrl,
        isAtHome: a.isAtHome,
        homeRadius: a.homeRadius
      }))
    ]
  };
}

export function useFilteredActivities({ activities, hubs, zoom }: UseFilteredActivitiesParams) {
  const mapActivities = useMemo(() => activities || [], [activities]);

  const ratingEnabled = isFeatureEnabled('RATING');

  const activitiesForListingCards = useMemo(() => {
    return mapActivities.map((act: Activity) => ({
      id: act.id,
      title: act.title,
      imageUrl: act.imageUrl,
      thumbnailUrl: act.thumbnailUrl,
      professionalName: act.professionalName,
      isAtHome: act.isAtHome,
      locationKinds: act.locationKinds,
      startingPrice: act.startingPrice,
      bookingsSinceLastYear: act.bookingsSinceLastYear || 0,
      city: extractCity(act.location?.address),
      hub: act.hub,
      ratingAverage: ratingEnabled ? act.rating?.ratingAverage : undefined,
      ratingTotal: ratingEnabled ? act.rating?.ratingTotal : undefined,
      availability: act.availability
    }));
  }, [mapActivities, ratingEnabled]);

  const atHomeActivities = useMemo(() => {
    return mapActivities
      .filter((activity) => activity.isAtHome)
      .map((a) => ({
        id: a.id,
        title: a.title,
        professionalName: a.professionalName,
        startingPrice: a.startingPrice,
        currency: 'CAD', // TODO: Get from activity if available
        imageUrl: a.thumbnailUrl,
        isAtHome: a.isAtHome,
        homeRadius: a.homeRadius
      }));
  }, [mapActivities]);

  const mapLocations = useMemo(() => {
    // Slot-level-locations (Section 4 C2 / F10): an activity can now have several
    // DISTINCT bookable slot locations, so we expand each activity into one POINT
    // per distinct location (from `locationSet`, falling back to the single
    // activity coordinate). Markers are built per location point; the existing
    // zoom clustering is reused unchanged. Within a marker the activity LIST is
    // de-duplicated to one row per activity (no longer automatic with many pins).
    // A point at the activity's Hub goes into the Hub's pin instead: one pin per Hub, holding
    // what is held there. Hub pins are never clustered with the others.
    const hubById = new Map((hubs ?? []).map((hub) => [hub.id, hub]));
    const atHub = new Map<string, Map<string, Activity>>();
    const points: LocationPoint[] = [];
    const addPoint = (activity: Activity, lat: number, lng: number) => {
      const hub = activity.hub ? hubById.get(activity.hub.id) : undefined;
      if (
        hub &&
        Math.abs(hub.coordinates.latitude - lat) < AT_HUB_TOLERANCE &&
        Math.abs(hub.coordinates.longitude - lng) < AT_HUB_TOLERANCE
      ) {
        const held = atHub.get(hub.id) ?? new Map<string, Activity>();
        held.set(activity.id, activity);
        atHub.set(hub.id, held);
        return;
      }
      points.push({ activity, lat, lng });
    };
    mapActivities.forEach((activity: Activity) => {
      if (activity.isAtHome) return; // at-home renders as a coverage area, not a pin
      const set = activity.locationSet && activity.locationSet.length > 0 ? activity.locationSet : undefined;
      if (set) {
        set.forEach((loc) => {
          if (loc.isAtHome) return;
          if (!Number.isFinite(loc.latitude) || !Number.isFinite(loc.longitude)) return;
          addPoint(activity, loc.latitude, loc.longitude);
        });
      } else if (activity.location?.coordinates) {
        const { latitude, longitude } = activity.location.coordinates;
        // A coordinate-less activity (online-only) defaults to 0/0 in the service
        // mapping - dropping a phantom pin in the Gulf of Guinea. No coords, no pin.
        if (!hasRealCoordinates(latitude, longitude)) return;
        addPoint(activity, latitude, longitude);
      }
    });
    const hubMarkers: MarkerLocation[] = (hubs ?? []).map((hub) =>
      buildHubMarker(hub, Array.from(atHub.get(hub.id)?.values() ?? []))
    );

    const clusterThreshold = calculateClusterThreshold(zoom);
    const useExactMatching = clusterThreshold < 0.01;

    if (useExactMatching) {
      const locationGroups = new Map<string, { lat: number; lng: number; points: LocationPoint[] }>();

      points.forEach((point) => {
        const locationKey = `${point.lat},${point.lng}`;
        const group = locationGroups.get(locationKey);
        if (group) {
          group.points.push(point);
        } else {
          locationGroups.set(locationKey, { lat: point.lat, lng: point.lng, points: [point] });
        }
      });

      return [
        ...hubMarkers,
        ...Array.from(locationGroups.entries()).map(([locationKey, group]) =>
          buildMarker(locationKey, group.lat, group.lng, group.points)
        )
      ];
    }

    const clusters: Array<{ points: LocationPoint[]; centerLat: number; centerLng: number }> = [];
    const processed = new Set<number>();

    points.forEach((point, index) => {
      if (processed.has(index)) return;

      const nearby: number[] = [index];
      processed.add(index);

      let changed = true;
      while (changed) {
        changed = false;

        const currentCenterLat = nearby.reduce((sum, i) => sum + points[i].lat, 0) / nearby.length;
        const currentCenterLng = nearby.reduce((sum, i) => sum + points[i].lng, 0) / nearby.length;

        points.forEach((other, otherIndex) => {
          if (processed.has(otherIndex)) return;
          const distance = calculateDistance(currentCenterLat, currentCenterLng, other.lat, other.lng);
          if (distance <= clusterThreshold) {
            nearby.push(otherIndex);
            processed.add(otherIndex);
            changed = true;
          }
        });
      }

      const centerLat = nearby.reduce((sum, i) => sum + points[i].lat, 0) / nearby.length;
      const centerLng = nearby.reduce((sum, i) => sum + points[i].lng, 0) / nearby.length;
      clusters.push({ points: nearby.map((i) => points[i]), centerLat, centerLng });
    });

    return [
      ...hubMarkers,
      ...clusters.map((cluster) =>
        buildMarker(
          `cluster_${cluster.centerLat.toFixed(6)}_${cluster.centerLng.toFixed(6)}`,
          cluster.centerLat,
          cluster.centerLng,
          cluster.points
        )
      )
    ];
  }, [mapActivities, hubs, zoom]);

  return {
    mapActivities,
    activitiesForListingCards,
    mapLocations,
    atHomeActivities
  };
}
